import moment from 'moment';
import lodashCompact from 'lodash/fp/compact';
import typeCheck from 'common/util/typeCheck';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import isBoolean from 'common/util/isBoolean';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN,
  MARKETING_PARTNER_LISTING_STATUS_REQUEST_WITHDRAWN,
  MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET,
  MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
  MARKETING_PARTNER_LISTING_TEMPORARILY_OFF_MARKET_REASON_OTHER,
  MARKETING_PARTNER_LISTING_WITHDRAWN_REASON_OTHER,
  PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY,
  PROPERTY_SETUP_ACCESS_TYPE_USE_ACCESS_CODE,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
  PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX,
  PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX,
  PROPERTY_SETUP_ACCESS_TYPE_HIDDEN,
  PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS,
  AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE,
} from 'common/marketingPartnerListingConstants';

import {
  makeListingAddressStreetDisplay,
} from 'common/listingHelpers';

import {
  LISTING_OCCUPANT_TYPE_VACANT,
  LISTING_PRELEASE_TIME_WINDOW_IN_DAYS,
  LISTING_SERVICE_TYPE_FULL_SERVICE,
  LISTING_SERVICE_TYPE_PRE_LEASE,
} from 'common/listingConstants';

import {
  JOB_STATUS_COMPLETED,
} from 'common/jobConstants';

import {
  petFieldsToNullify,
} from 'server/models/Listing/fieldgroups';

export function cleanPropertyAccessFields(formValues) {
  if (formValues.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE) {
    formValues.propertySetupAccessType = PROPERTY_SETUP_ACCESS_TYPE_USE_ACCESS_CODE;
    formValues.keyPickupDescription = null;
    formValues.supraCbsCode = null;
    formValues.dateKeyExpectedAtListing = null;
  }
  else if (formValues.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY) {
    formValues.propertyAccessCode = null;
  }

  if (formValues.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY) {
    formValues.keyPickupDescription = null;
    formValues.dateKeyExpectedAtListing = null;
    formValues.supraCbsCode = null;
  }
  else {
    formValues.propertyAccessContactName = null;
    formValues.propertyAccessContactPhone = null;
    formValues.propertyAccessContactEmail = null;
  }

  if (formValues.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX) {
    formValues.keyPickupDescription = null;
  }

  if (formValues.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX) {
    formValues.keyPickupDescription = null;
    formValues.supraCbsCode = null;
  }

  if (formValues.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_HIDDEN) {
    formValues.supraCbsCode = null;
  }
}

export function cleanMarketingPartnerListingFormValues(formValues) {
  formValues.petsAllowed = formValues.dogsAllowed || formValues.catsAllowed;

  if (!isBoolean(formValues.petsAllowed)) {
    delete formValues.petsAllowed;
  }

  if (formValues.addressStreetName) {
    formValues.addressStreetDisplay = makeListingAddressStreetDisplay(formValues);
  }

  if (formValues.petsAllowed) {
    formValues.petWeight = Number(formValues.petWeight);
    formValues.petFee = formValues.petFee || 0;
    formValues.petDeposit = formValues.petDeposit || 0;
    formValues.monthlyPetRent = formValues.monthlyPetRent || 0;
  }

  if (!formValues.dogsAllowed) {
    delete formValues.petWeight;
  }

  if (formValues.petsAllowed === false) {
    for (const petFieldId of petFieldsToNullify) {
      if (petFieldId in formValues) {
        formValues[petFieldId] = null;
      }
    }
  }

  if (formValues.occupantType === LISTING_OCCUPANT_TYPE_VACANT) {
    formValues.vacantDate = null;
    formValues.occupantName = null;
    formValues.occupantPhone = null;
    formValues.occupantEmail = null;
  }

  if (formValues.requireSunlock) {
    formValues.noRequireSunlockReason = null;
    formValues.sunlockAllowed = true;
  }
  else if (formValues.requireSunlock === false) {
    formValues.sunlockAllowed = false;
  }

  if (formValues.requirePhotos) {
    formValues.linkToExistingPhotos = null;
  }
  else if (
    formValues.requirePhotos === false
    && formValues.requireVirtual3dTour === false
  ) {
    formValues.availableForPhotoDate = null;
    formValues.availableForPhotoDateSchedule = null;
    formValues.availableForPhotoContactName = null;
    formValues.availableForPhotoContactPhone = null;
  }

  cleanPropertyAccessFields(formValues);

  if (formValues.status) {
    if (formValues.status !== MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET) {
      formValues.temporarilyOffMarketReason = null;
      formValues.temporarilyOffMarketReasonProvided = null;
    }

    if (formValues.status !== MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN
      && formValues.status !== MARKETING_PARTNER_LISTING_STATUS_REQUEST_WITHDRAWN
    ) {
      formValues.withdrawalReason = null;
      formValues.withdrawalReasonProvided = null;
    }

    if (formValues.temporarilyOffMarketReason !== MARKETING_PARTNER_LISTING_TEMPORARILY_OFF_MARKET_REASON_OTHER) { // eslint-disable-line max-len
      formValues.temporarilyOffMarketReasonProvided = null;
    }

    if (formValues.withdrawalReason !== MARKETING_PARTNER_LISTING_WITHDRAWN_REASON_OTHER) {
      formValues.withdrawalReasonProvided = null;
    }
  }

  if (formValues.isPreleasingListing) {
    formValues.services = lodashCompact([
      ...formValues.services,
      LISTING_SERVICE_TYPE_FULL_SERVICE,
      LISTING_SERVICE_TYPE_PRE_LEASE,
    ]);

    formValues.availableForPhotoDate = moment()
      .add(1, 'days')
      .startOf('day')
      .toISOString();

    formValues.availableForSetupDate = moment(formValues.preleaseStartDate)
      .add(LISTING_PRELEASE_TIME_WINDOW_IN_DAYS, 'days')
      .toISOString();
  }
  return setNullEmptyValues(formValues);
}

export function getPreLeasingDueDate({ availableForSetupDate, hasMediaJobs }) {
  typeCheck('availableForSetupDate::DateString', availableForSetupDate);
  typeCheck('hasMediaJobs::Boolean', hasMediaJobs);

  const daysToAdd = hasMediaJobs ? 3 : 1;
  const dueDate = momentWithAustinTimezone(availableForSetupDate)
    .add(daysToAdd, 'days');

  // Is due date is Sunday or Monday add extra day
  if (dueDate.day() === 0 || dueDate.day() === 1) {
    return dueDate.add(1, 'days');
  }

  return dueDate;
}

export function getJobOrderDueDate(availableForSetupDate) {
  typeCheck('availableForSetupDate::DateString', availableForSetupDate);

  const dueDate = momentWithAustinTimezone(availableForSetupDate);
  const now = momentWithAustinTimezone();

  // If due date is Today then add extra day
  if (dueDate.isSame(now, 'day')) {
    dueDate.add(1, 'day');
  }

  // If due date is Sunday then add extra day
  if (dueDate.day() === 0) {
    dueDate.add(1, 'day');
  }

  return dueDate;
}

export function getDueDate(availableForSetupDate) {
  typeCheck('availableForSetupDate::DateString', availableForSetupDate);

  const dueDate = momentWithAustinTimezone(availableForSetupDate)
    .add(3, 'days');

  // Is due date is Sunday or Monday add extra day
  if (dueDate.day() === 0 || dueDate.day() === 1) {
    return dueDate.add(1, 'days');
  }

  return dueDate;
}

export function getDueDateDisplay(marketingPartnerListing, listing) {
  typeCheck('marketingPartnerListing::Maybe NonEmptyObject', marketingPartnerListing);
  typeCheck('listing::Maybe NonEmptyObject', listing);

  if (!marketingPartnerListing
    || listing?.walkthroughIssuesResolved === false
    || marketingPartnerListing.status !== MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS
    || !marketingPartnerListing.dueDate
  ) {
    return 'N/A';
  }

  return moment(marketingPartnerListing.dueDate).format('MM/DD/YYYY');
}

export function generatePropertyAccessInstructions(listing, isMedia = false) {
  let instructions = '';
  if (listing.propertyAccessCode && listing.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE) {
    instructions += `Use access code: ${listing.propertyAccessCode}`;
  }
  else if (
    listing.propertySetupAccessType
    && listing.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_USE_ACCESS_CODE
  ) {
    if (listing.dateKeyExpectedAtListing) {
      instructions += 'Date key will be at listing: ' +
        `${momentWithAustinTimezone(listing.dateKeyExpectedAtListing).format('MM/DD/YYYY')}\n\n`;
    }
    const keyAccessLabel = PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS.find(
      ({ value }) => value === listing.propertySetupAccessType
    )?.label;

    instructions += `Key Access Type: ${keyAccessLabel}\n`;

    if (listing.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_HIDDEN) {
      instructions += `Key location: ${listing.keyPickupDescription}`;
    }
    else if (listing.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX) {
      instructions += `Owner/Agent Lockbox Code is: ${listing.lockboxCode}`;
    }
    else if (listing.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX) {
      instructions += `Owner/Agent Supra CBS Code is: ${listing.supraCbsCode}`;
    }
    else if (listing.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY) {
      instructions += `Key Contact: ${[
        listing.propertyAccessContactName,
        listing.propertyAccessContactPhone,
        listing.propertyAccessContactEmail,
      ].join(', ')}`;
    }
  }

  if (isMedia
    && listing.availableForPhotoDateSchedule === AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE
  ) {
    instructions += '\n\nCall for Photo Scheduling\n';
    instructions += `Photo Scheduling Contact Name: ${listing.availableForPhotoContactName}\n`;
    instructions += `Photo Scheduling Contact Phone: ${listing.availableForPhotoContactPhone}\n`;
  }

  return instructions;
}

export function getCoListingAgent(coListingAgentMlsNumber, partnerUsers) {
  let coListingAgent;

  if (partnerUsers && coListingAgentMlsNumber) {
    coListingAgent = partnerUsers.find((partnerUser) => {
      return partnerUser.agentMlsNumber === coListingAgentMlsNumber;
    });
  }

  return coListingAgent;
}

export function getGoingToMarketCheckList(marketingListing) {
  typeCheck('marketingPartnerListing::NonEmptyObject', marketingListing);

  const {
    listing,
    shouldRunCMA,
    yardSignAllowed,
    yardSignSetupJob,
    selfShowingHardwareInstallJob,
    brokerPriceOpinionPdfUrl,
    requireSelfShowingHardware,
    marketingInfoRequiredFromOwner,
    marketingInfoCollectedFromOwner,
    requirePropertyConditionReport,
    propertyConditionOverall,
    propertyConditionUrl,
    propertyConditionDateInspected,
    marketingItemsEmailSentToPartner,
    markedInstallAsCompletedDate,
    reassignedByPartnerDate,
  } = marketingListing;
  const {
    price,
    photoUrls,
    description,
    requirePhotos,
    comparativeMarketAnalysisNotes,
    comparativeMarketAnalysisPdfUrl,
    comparativeMarketAnalysisRentPrice,
  } = listing;

  const shouldHidePropertyConditionReport = !requirePropertyConditionReport;
  const shouldHideMarketingItems = !yardSignAllowed && !requireSelfShowingHardware;
  const shouldHidePhotos = !requirePhotos;
  const photosCompleted = photoUrls.length > 0;
  const BPOCompleted = !!shouldRunCMA && brokerPriceOpinionPdfUrl != null;
  const CMACompleted = !!shouldRunCMA
    && comparativeMarketAnalysisRentPrice != null
    && comparativeMarketAnalysisPdfUrl != null
    && comparativeMarketAnalysisNotes != null;
  const yardSignCompleted = yardSignAllowed
    ? yardSignSetupJob?.status === JOB_STATUS_COMPLETED
    : true;
  const selfShowingHardwareCompleted = requireSelfShowingHardware
    ? selfShowingHardwareInstallJob?.status === JOB_STATUS_COMPLETED
    : true;
  const marketingItemsCompleted = (yardSignCompleted && selfShowingHardwareCompleted)
    || !!markedInstallAsCompletedDate;
  const descriptionCompleted = description != null;
  const marketingInfoRequiredCompleted = marketingInfoCollectedFromOwner != null;
  const propertyConditionReportCompleted = propertyConditionOverall != null
    && propertyConditionUrl != null
    && propertyConditionDateInspected != null;

  return {
    price,
    CMACompleted,
    BPOCompleted,
    shouldRunCMA: !!shouldRunCMA,
    photosCompleted,
    marketingItemsCompleted,
    shouldHideMarketingItems,
    descriptionCompleted,
    shouldHidePhotos,
    shouldCollectInfoFromOwner: !!marketingInfoRequiredFromOwner,
    marketingInfoRequiredCompleted,
    shouldHidePropertyConditionReport,
    propertyConditionReportCompleted,
    emailSentToPartnerCompleted: reassignedByPartnerDate && shouldHideMarketingItems
      ? true
      : !!marketingItemsEmailSentToPartner,
  };
}

export const getLastUninstallJobOrder = ({ marketingPartnerListing, uninstallJobIds }) => {
  const marketingItems = [];

  for (const uninstallJobId of uninstallJobIds) {
    const uninstallJob = marketingPartnerListing[uninstallJobId];
    if (uninstallJob) {
      marketingItems.push(uninstallJob);
    }
  }

  marketingItems.sort((jobA, jobB) => (
    new Date(jobB.createdDate).getTime() - new Date(jobA.createdDate).getTime()
  ));

  return marketingItems[0];
};
